import { useCallback, useEffect, useRef } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { PricingPercentSyncDocument } from "@/generated/graphql";

const SYNC_DEBOUNCE_MS = 150;

export function usePricingPercentSync() {
  const [syncPercents] = useLazyQuery(PricingPercentSyncDocument, {
    fetchPolicy: "network-only",
  });
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const scheduleSync = useCallback(
    (
      effectiveCostCents: number,
      sourceField: "MARKUP" | "TARGET_NET_MARGIN",
      value: number,
      taxPercent: number | null,
      onResult: (result: { markupPercent: number; targetNetMarginPercent: number } | null) => void
    ) => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        void syncPercents({
          variables: {
            input: {
              effectiveCostCents,
              sourceField,
              value,
              taxPercent,
            },
          },
        }).then(({ data }) => {
          onResult(data?.pricingPercentSync ?? null);
        });
      }, SYNC_DEBOUNCE_MS);
    },
    [syncPercents]
  );

  const syncFromMarkup = useCallback(
    (
      effectiveCostCents: number,
      markup: number,
      taxPercent: number | null,
      onResult: (margin: number | null) => void
    ) => {
      scheduleSync(effectiveCostCents, "MARKUP", markup, taxPercent, (result) => {
        onResult(result?.targetNetMarginPercent ?? null);
      });
    },
    [scheduleSync]
  );

  const syncFromMargin = useCallback(
    (
      effectiveCostCents: number,
      margin: number,
      taxPercent: number | null,
      onResult: (markup: number | null) => void
    ) => {
      scheduleSync(effectiveCostCents, "TARGET_NET_MARGIN", margin, taxPercent, (result) => {
        onResult(result?.markupPercent ?? null);
      });
    },
    [scheduleSync]
  );

  return {
    syncFromMarkup,
    syncFromMargin,
  };
}
